

import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizActionObj, WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { BusinessException, EntityModificationPack } from '@wizeoo/akm-message-lib';
import { cloneDeep } from 'lodash-es';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { FeatureVO } from '../../vo/core/feature.vo';
import { RoleVO } from '../../vo/user/role.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { UserVO } from '../../vo/user/user.vo';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ProfileVO } from '../../vo/user/profile.vo';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';

/** gestion des rôles... */
export class RoleEngine {

  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }


  public fromWizActionObj(pObj: WizActionObj): RoleVO {
    const result = new RoleVO(this.model);
    result.code = pObj.buttonCode;
    return result;
  }

  /**
   * Retourne la MEME instance de role que dans la liste du modèle.
   * @param source : RoleVO cherché
   * @return RoleVO le role récupéré de la map, null s'il n'y est pas
   */
  public getCurrentInstance(source: RoleVO): RoleVO {
    let result: RoleVO = null;
    if (source && this.model.rolesFastMap) {
      result = this.model.rolesFastMap.get(this.getRoleKey(source));
    }
    return result;
  }

  /**
   * Création d'un role
   * @return RoleVO Le rôle créé
   */
  public createObjectRole(): RoleVO {
    const role = new RoleVO(this.model);
    // role.domain = this.model.defaultUserBU;
    role.domain = this.model.domainCode;
    role.code = null;
    role.label = null;
    role.businessUnitCode = this.model.businessUnitCode;
    return role;
  }

  /**
   * Clone de role
   * @param role Le role à cloner
   * @return le role cloné
   */
  public clone(role: RoleVO): RoleVO {
    const clone = new RoleVO(this.model, cloneDeep(role.source));
    return clone;
  }


  /**
   * Recherche le role à sélectionner par défaut
   * @return RoleVO le role par défaut
   */
  public getDefaultRole(): RoleVO {
    let result: RoleVO = null;
    if (this.model.currentEditedRole) { // dernier role édité
      result = this.getCurrentInstance(this.model.currentEditedRole);
    } else { // rien d'actif, recherche d'un role du contexte
      // const contextRole: UserContextVO = ContextEngine.getContext(CONTEXT_RANGE.CONTEXT_TAB, CONTEXT.SELECTED_ITEM);
      // if (contextRole) {
      //   result = this.model.rolesFastMap.get(contextRole.valor);
      // }
    }
    return result;
  }

  public getRoleKey(role: RoleVO): string {
    return [role.domain, role.businessUnitCode, role.visionCode, role.code].join(ViewData.KEY_SEPARATOR);
  }

  /**
   * Retourne le rôle associé aux paramètres depuis la map des rôles
   * @param domain Le domaine
   * @param bu La bu
   * @param vision La vision
   * @param code Le code du rôle
   * @returns Le role ou null si il n'existe pas
   */
  public getRoleByCode(domain: string, bu: string, vision: string, code: string): RoleVO {
    return this.model.rolesFastMap.get([domain, bu, vision, code].join(ViewData.KEY_SEPARATOR));
  }



  /**
   * Retourne tous les rôles associés à une feature donnée
   * @param domain Le domaine
   * @param bu La bu
   * @param vision La vision
   * @param code Le code de la feature
   * @returns La collection de role associés à la feature
   */
  // public getRoleForFeature(domain: string, bu: string, vision: string, featureCode: string): RoleVO[] {
  //   // TODO TODO 
  //   return null; this.model.rolesFeatureMap.get([domain, bu, vision, featureCode].join(ViewData.KEY_SEPARATOR));
  // }

  /**
   * Vérification du lien entre le rôle et la feature
   * @param role le rôle
   * @param feature la feature concernée
   * @returns true si le role a comme feature code la feature
   */
  public static hasFeature(role: RoleVO, feature: FeatureVO): boolean {
    if (!!role && !!feature) {
      return role.featureCode === feature.code;
    }
    return false;
  }
  /**
   * Validation de l'objet
   * - Un rôle peut être sauvegardé et ajouté si le label et le code sont renseignés alors qu’aucun rôle ni feature ne sont définis
   * @param role Le role à valider
   * @return boolean true si le role est conforme, false sinon
   */
  public isValid(role: RoleVO): boolean {
    if (!role) {
      return false;
    }

    if (role.code === undefined || role.code === null) {
      return false;
    }

    if (role.domain === undefined || role.domain === null) {
      return false;
    }

    if (role.businessUnitCode === undefined || role.businessUnitCode === null) {
      return false;
    }

    return true;
  }

  /**
   * vérification de la possibilité de suppression
   * - Dans le cas où le rôle est utilisé dans un profil ou par un utilisateur dans l’application, le rôle ne peut être supprimé.
   * @return boolean true si le est utilisé, false sinon
   */
  public isUsedRole(role: RoleVO): boolean {
    const currentRole: RoleVO = this.getCurrentInstance(role);
    const profiles = this.getProfilesForRole(currentRole);
    const users = this.getUsersForRole(currentRole);
    return (profiles && profiles.length > 0) || (users && users.length > 0);
  }

  /**
   * récupération de la liste des profils utilisant le rôle passé en paramètre
   * @return ProfileVO[] une liste de profils
   */
  public getProfilesForRole(role: RoleVO): ProfileVO[] {
    if (this.model.profileFastMap) {
      return Array.from(this.model.profileFastMap.values()).filter((profile: ProfileVO) => profile.hasRole(role));
    }
    return [];
  }

  /**
   * récupération de la liste des users utilisant le rôle passé en paramètre
   * @return UserVO[] la liste de users
   */
  public getUsersForRole(role: RoleVO): UserVO[] {
    if (this.model.usersMap) {
      return Array.from(this.model.usersMap.values()).filter((user: UserVO) => this.model.userEngine.hasRole(user, role, null));
    }
    return [];
  }

  /**
   * vérification que au moins l'un des droits est activé
   * @param role, le role à controler
   * @returns true si un droit au moins est setter, false si aucun
   */
  public roleHasAccess(role: RoleVO): boolean {
    return !!role.doCreate || !!role.doRead || !!role.doDelete || !!role.doUpdate || !!role.doNone;
  }

  /**
   * vérification que tous les droits sont sélectionnés
   * @param role, le role à controler
   * @returns true si tous les droits sont sélectionnés, false si aucun
   */
  public allAccessSelected(role: RoleVO): boolean {
    return role.doCreate && role.doRead && role.doDelete && role.doUpdate;
  }



  // ######################### crud  ##############################


  /**
   * create/ update d'un role
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: RoleVO, from: RoleVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) { // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      // retour en vue
      callback(to, WIZ_COMMAND_STATE.SUCCESS);
    }
  }


  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: any = cmd.sourceVO[0];
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });

      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const modelRole: RoleVO = this.model.rolesFastMap.get(source.code);
            if (!!modelRole) {
              modelRole.source = source;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.model.rolesFastMap.set(vo.code, vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this.model.rolesFastMap.delete(vo.code);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }

  /**
   * Suppression d'un role
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: RoleVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
  }


  public get rolesList(): RoleVO[] {
    let result: RoleVO[] = [];
    // TODO TODO  optimiser. Inutile de boucler sur tout

    let srcMap: Map<string, Map<string, Map<string, RoleVO>>> = this.model.rolesMap;
    [...srcMap.values()].forEach(srcSubMap  => {
      [...srcSubMap.values()].forEach(srcSubMap2  => {
        [...srcSubMap2.values()].forEach(role  => {
          result.push(role);
        });
      });
    });

    // console.log('srcMap', result);
    return result;
  }

}
